import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { updateLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { formatTokenAmount } from '@/utils/tokenConfig';
import { AlertTriangle } from 'lucide-react';

interface EarlyUnlockDialogProps {
  lock: LocksResponse | null;
  lockId: string;
  open: boolean;
  onClose: () => void;
}

export const EarlyUnlockDialog: React.FC<EarlyUnlockDialogProps> = ({
  lock,
  lockId,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);

  if (!lock) return null;

  const penaltyAmt = Math.floor(lock.lkAmt * 0.01);
  const receiveAmt = lock.lkAmt - penaltyAmt;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const success = await updateLocks(lockId, { status: 'earlyUnlocked' });
      if (success) {
        toast.success('Early unlock executed', {
          description: `You received ${formatTokenAmount(receiveAmt, lock.tokenMint)}.`,
        });
        onClose();
      } else {
        toast.error('Early unlock failed', {
          description: 'Transaction was denied by the vault.',
        });
      }
    } catch (e) {
      toast.error('Something went wrong', { description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <AlertDialogContent
        style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'hsl(0 65% 48% / 0.15)', border: '1px solid hsl(0 65% 48% / 0.3)' }}
            >
              <AlertTriangle size={18} style={{ color: 'hsl(0 65% 60%)' }} />
            </div>
            <AlertDialogTitle
              className="text-xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Break the Lock?
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription
            className="text-sm leading-relaxed"
            style={{ color: 'hsl(35 8% 60%)' }}
          >
            You're unlocking early. The vault charges a 1% penalty for breaking your commitment.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Math breakdown */}
        <div
          className="rounded-lg p-4 space-y-2 text-sm my-2"
          style={{ background: 'hsl(30 5% 8%)', border: '1px solid hsl(35 12% 12%)' }}
        >
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Locked amount</span>
            <span>{formatTokenAmount(lock.lkAmt, lock.tokenMint)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Early unlock penalty (1%)</span>
            <span style={{ color: 'hsl(0 65% 55%)' }}>-{formatTokenAmount(penaltyAmt, lock.tokenMint)}</span>
          </div>
          <div
            className="flex justify-between pt-2 font-semibold"
            style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(43 75% 68%)' }}
          >
            <span>You receive</span>
            <span>{formatTokenAmount(receiveAmt, lock.tokenMint)}</span>
          </div>
        </div>

        <p className="text-xs" style={{ color: 'hsl(35 8% 45%)' }}>
          The penalty ({formatTokenAmount(penaltyAmt, lock.tokenMint)}) is forfeited. This action
          is irreversible.
        </p>

        <AlertDialogFooter className="gap-3 mt-2">
          <AlertDialogCancel
            disabled={loading}
            onClick={onClose}
            style={{ background: 'hsl(30 5% 12%)', border: '1px solid hsl(35 12% 18%)', color: 'hsl(40 15% 80%)' }}
          >
            Keep Holding
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            onClick={handleConfirm}
            style={{ background: 'hsl(0 65% 48%)', color: 'white', border: 'none' }}
          >
            {loading ? 'Processing...' : 'Break Lock (-1%)'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EarlyUnlockDialog;
